import React from "react"
import AvatarFallback from "./avatar-fallback"
import AvatarImage from "./avatar-image"
import { AvatarSize } from "./types"

const getInitials = (name?: string) => {
  if (!name) return ""

  const parts = name.trim().split(/\s+/)
  if (parts.length === 1) {
    return parts[0].substring(0, 2).toUpperCase()
  }
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

export type { AvatarSize }

export type AvatarProps = {
  /** URL for the avatar image (displays image when provided) */
  avatarUrl?: string
  /** User's display name (used for initials in fallback) */
  name?: string
  /** Additional CSS classes to apply to the avatar */
  className?: string
  /** Whether the avatar is active (dims when false) */
  active?: boolean
  /** Size of the avatar */
  size?: AvatarSize
  "data-slot"?: string
}

/**
 * Displays a user avatar as an image or colored initials fallback.
 * Supports two sizes and an active/inactive state.
 */
const Avatar = ({
  avatarUrl,
  name,
  className,
  active = true,
  size = "sm",
  "data-slot": dataSlot,
  ...props
}: AvatarProps & React.HTMLAttributes<HTMLSpanElement>) => {
  if (avatarUrl) {
    return (
      <span className="relative" data-slot={dataSlot} {...props}>
        <AvatarImage
          src={avatarUrl}
          alt={name}
          className={className}
          size={size}
        />
        {!active && (
          <span className="absolute inset-0 bg-gray-700 opacity-50 rounded-full" />
        )}
      </span>
    )
  }

  const initials = getInitials(name)

  return (
    <span className="relative" data-slot={dataSlot} {...props}>
      <AvatarFallback name={name} className={className} size={size}>
        {initials}
      </AvatarFallback>
      {!active && (
        <span className="absolute inset-0 bg-gray-700 opacity-50 rounded-full" />
      )}
    </span>
  )
}

export { Avatar, AvatarFallback, AvatarImage }
export default Avatar
